import { useState, useMemo, useEffect } from 'react';
import SlackLogo from '../../../../../assets/slack-logo.svg';
import SlackLogoPrimary from '../../../../../assets/slack-logo-primary.svg';
import SharedDialog from '../../../../../shared/shared-dialog';
import CollectionsSharedTransferMultiSelect from '../../../../../shared/collections-shared-transfer-multi-select';
import { useStore } from '../../../../state/zustand-store';
import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import Stack from '@mui/material/Stack';
import { SxProps, Theme, useTheme } from '@mui/material/styles';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
export interface SlackChannel {
  id: string;
  name: string;
  private?: boolean;
  numOfMembers?: number;
}

export interface SlackCollection {
  id: string;
  name: string;
  description: string;
  channels: SlackChannel[];
  createTime: string;
  updateTime?: string;
}

export interface CreateSlackCollectionDialogProps {
  open: boolean;
  handleClose: () => void;
  selectedChannelIds?: string[];
  collectionToEdit?: SlackCollection | null;
  collection?: SlackCollection | null;
  allChannels: SlackChannel[];
  onCreateCollection?: (collection: SlackCollection) => void;
  onUpdateCollection?: (collection: SlackCollection) => void;
}

export default function CreateSlackCollectionDialog({
  open,
  handleClose,
  selectedChannelIds = [],
  collectionToEdit,
  collection,
  allChannels,
  onCreateCollection,
  onUpdateCollection,
}: Readonly<CreateSlackCollectionDialogProps>) {
  const { setSnackbar } = useStore();
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === 'dark';

  const activeInputStyles: SxProps<Theme> = isDarkMode
    ? {
        '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline':
          {
            borderColor: theme.palette.semantic.outline.primary,
          },
        '& .MuiInputLabel-root.Mui-focused': {
          color: theme.palette.semantic.text.title3,
        },
      }
    : {};

  const editTarget = collectionToEdit || collection;
  const isEditMode = Boolean(editTarget);

  const [collectionName, setCollectionName] = useState('');
  const [collectionDescription, setCollectionDescription] = useState('');
  const [refinedChannelIds, setRefinedChannelIds] = useState<string[]>([]);

  useEffect(() => {
    if (open) {
      if (editTarget) {
        setCollectionName(editTarget.name);
        setCollectionDescription(editTarget.description);
        setRefinedChannelIds(
          editTarget.channels ? editTarget.channels.map((c) => c.id) : []
        );
      } else {
        setCollectionName('');
        setCollectionDescription('');
        setRefinedChannelIds(selectedChannelIds);
      }
    }
  }, [open]);

  const mergedChannels = useMemo(() => {
    const map = new Map<string, SlackChannel>();
    if (editTarget?.channels) {
      editTarget.channels.forEach((channel) => map.set(channel.id, channel));
    }
    allChannels.forEach((channel) => {
      if (!map.has(channel.id)) {
        map.set(channel.id, channel);
      }
    });
    return Array.from(map.values());
  }, [allChannels, editTarget]);

  const selectedChannels = useMemo(
    () =>
      mergedChannels.filter((channel) =>
        refinedChannelIds.includes(channel.id)
      ),
    [mergedChannels, refinedChannelIds]
  );

  const handleSave = () => {
    if (isEditMode && editTarget) {
      const updatedCollection: SlackCollection = {
        ...editTarget,
        name: collectionName.trim(),
        description: collectionDescription,
        channels: selectedChannels,
        updateTime: new Date().toISOString(),
      };

      if (onUpdateCollection) {
        onUpdateCollection(updatedCollection);
      } else if (onCreateCollection) {
        onCreateCollection(updatedCollection);
      }

      setSnackbar({
        open: true,
        message: `Successfully updated collection "${collectionName}"!`,
        severity: 'success',
      });
    } else {
      const newCollection: SlackCollection = {
        id: `collection-${Date.now()}`,
        name: collectionName.trim(),
        description: collectionDescription,
        channels: selectedChannels,
        createTime: new Date().toISOString(),
      };

      if (onCreateCollection) {
        onCreateCollection(newCollection);
      }

      setSnackbar({
        open: true,
        message: `Successfully created collection "${collectionName}"!`,
        severity: 'success',
      });
    }

    handleClose();
  };

  return (
    <SharedDialog
      open={open}
      handleClose={handleClose}
      title={isEditMode ? 'Edit Slack Collection' : 'Create Slack Collection'}
      maxWidth="md"
      dialogContent={
        <Stack gap={2}>
          <Stack direction={{ xs: 'column', sm: 'row' }} gap={2}>
            <TextField
              autoFocus
              required
              id="collectionName"
              label="Slack Collection Name"
              fullWidth
              autoComplete="off"
              value={collectionName}
              onChange={(event) => setCollectionName(event.target.value)}
              sx={activeInputStyles}
            />
            <TextField
              id="collectionDescription"
              label="Description"
              fullWidth
              autoComplete="off"
              value={collectionDescription}
              onChange={(event) => setCollectionDescription(event.target.value)}
              sx={activeInputStyles}
            />
          </Stack>
          <Divider />
          <CollectionsSharedTransferMultiSelect
            options={mergedChannels}
            selectedIds={refinedChannelIds}
            onChange={setRefinedChannelIds}
            listTitle="Slack Channels List"
            listSubtitle="Select the Slack channels to include in this collection."
            selectedTitle="Selected Slack Channels"
            selectedSubtitle="Remove channels using the close button."
            searchPlaceholder="Search for a Slack channel"
            columnLabelName="Slack Channels"
            columnLabelType="Channel Type"
            itemIcon={
              <Box
                component="img"
                src={SlackLogoPrimary}
                alt=""
                sx={{
                  width: 20,
                  height: 20,
                  flexShrink: 0,
                  filter: isDarkMode ? 'brightness(0) invert(1)' : 'none',
                }}
              />
            }
            emptySelectedMessage={
              <Typography
                variant="body2"
                color="text.secondary"
                component="div"
              >
                <Box
                  sx={{
                    width: 72,
                    height: 72,
                    display: 'grid',
                    placeItems: 'center',
                    mx: 'auto',
                    mb: 2,
                    borderRadius: '50%',
                    bgcolor: 'background.paper',
                  }}
                >
                  <Box
                    component="img"
                    src={SlackLogo}
                    alt=""
                    sx={{ width: 48, height: 48 }}
                  />
                </Box>
                There aren’t any Slack channels listed yet.
                <br />
                Please select Slack channels on the left column.
              </Typography>
            }
          />
        </Stack>
      }
      primaryButton={{
        title: 'Save',
        action: handleSave,
        disabled: !collectionName.trim(),
      }}
    />
  );
}
